import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, ShoppingCart, LogOut, User } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';

const ProDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('pro_token');
    const userData = localStorage.getItem('pro_user');

    if (!token || !userData) {
      navigate('/pro/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('pro_token');
    localStorage.removeItem('pro_user');
    navigate('/pro/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Logo showTagline={true} clickable={true} />
            <div className="flex items-center gap-4">
              <span className="text-gray-700">{user?.prenom} {user?.nom}</span>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
              >
                <LogOut size={20} />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-celadon to-celadon-light text-white rounded-lg p-8 mb-8 shadow-lg">
          <h1 className="text-3xl font-bold mb-2">Bienvenue dans votre espace PRO 🇫🇷</h1>
          <p className="text-lg opacity-90">{user?.company_name}</p>
          <div className="mt-6 flex items-center gap-8">
            <div className="bg-white bg-opacity-20 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-sm opacity-90 mb-1">Votre remise personnalisée</p>
              <p className="text-4xl font-bold">{user?.discount_percentage}%</p>
            </div>
            <div className="text-sm opacity-90">
              <p>✅ Tarifs préférentiels actifs</p>
              <p>✅ Accès aux documents exclusifs</p>
              <p>✅ Devis personnalisés</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <button
            onClick={() => navigate('/')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-celadon"
          >
            <div className="flex items-center gap-4">
              <div className="bg-celadon bg-opacity-10 p-3 rounded-lg">
                <ShoppingCart size={32} className="text-celadon" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg text-gray-900">Catalogue Produits</h3>
                <p className="text-sm text-gray-600">Parcourir avec vos tarifs PRO</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/pro/documents')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-celadon"
          >
            <div className="flex items-center gap-4">
              <div className="bg-celadon bg-opacity-10 p-3 rounded-lg">
                <FileText size={32} className="text-celadon" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg text-gray-900">Documents & Catalogues</h3>
                <p className="text-sm text-gray-600">Accès aux ressources PRO</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/pro/devis')}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all border-2 border-transparent hover:border-celadon"
          >
            <div className="flex items-center gap-4">
              <div className="bg-celadon bg-opacity-10 p-3 rounded-lg">
                <Download size={32} className="text-celadon" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-lg text-gray-900">Demande de Devis PRO</h3>
                <p className="text-sm text-gray-600">Devis personnalisés</p>
              </div>
            </div>
          </button>
        </div>

        {/* Account Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <User size={24} className="text-celadon" />
            Informations du compte
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Société</p>
              <p className="font-medium text-gray-900">{user?.company_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">SIRET</p>
              <p className="font-medium text-gray-900">{user?.siret}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Contact</p>
              <p className="font-medium text-gray-900">{user?.prenom} {user?.nom}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Email</p>
              <p className="font-medium text-gray-900">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Téléphone</p>
              <p className="font-medium text-gray-900">{user?.phone}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Activité</p>
              <p className="font-medium text-gray-900">{user?.activity}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-sm text-gray-600 mb-1">Adresse</p>
              <p className="font-medium text-gray-900">
                {user?.address}, {user?.postal_code} {user?.city}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProDashboard;
